import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'react-bootstrap-icons';
import * as pdfjs from 'pdfjs-dist';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type Props = {
  blob: Blob;
};

export default function SecureResumePdfViewer({ blob }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scale, setScale] = useState(1.1);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    const render = async () => {
      setLoading(true);
      setError('');
      container.innerHTML = '';

      try {
        const buffer = await blob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: buffer }).promise;
        if (cancelled) return;

        setNumPages(pdf.numPages);
        const safePage = Math.min(Math.max(page, 1), pdf.numPages);
        const pdfPage = await pdf.getPage(safePage);
        const viewport = pdfPage.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.className = 'resume-preview-page-canvas';
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Canvas unavailable');
        }
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await pdfPage.render({ canvasContext: context, viewport }).promise;
        if (!cancelled) {
          container.appendChild(canvas);
        }
      } catch {
        if (!cancelled) {
          setError('Could not render resume preview.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void render();

    return () => {
      cancelled = true;
    };
  }, [blob, page, scale]);

  useEffect(() => {
    const blockPrintShortcuts = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('keydown', blockPrintShortcuts, true);
    return () => document.removeEventListener('keydown', blockPrintShortcuts, true);
  }, []);

  return (
    <div className="secure-resume-pdf-viewer">
      <div className="secure-resume-pdf-viewer__toolbar">
        <div className="secure-resume-pdf-viewer__pager">
          <Button
            variant="outline-light"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <ChevronLeft size={14} />
          </Button>
          <span>
            Page {page} of {numPages || 1}
          </span>
          <Button
            variant="outline-light"
            size="sm"
            disabled={numPages === 0 || page >= numPages}
            onClick={() => setPage((current) => Math.min(numPages, current + 1))}
          >
            <ChevronRight size={14} />
          </Button>
        </div>
        <div className="secure-resume-pdf-viewer__zoom">
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => setScale((current) => Math.max(0.8, current - 0.1))}
          >
            <ZoomOut size={14} />
          </Button>
          <span>{Math.round(scale * 100)}%</span>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => setScale((current) => Math.min(2, current + 0.1))}
          >
            <ZoomIn size={14} />
          </Button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="secure-resume-pdf-viewer__canvas-wrap"
        onContextMenu={(event) => event.preventDefault()}
      />
      {loading ? <div className="secure-resume-pdf-viewer__loading">Rendering resume…</div> : null}
      {error ? <div className="secure-resume-pdf-viewer__error">{error}</div> : null}
    </div>
  );
}
